import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "@helpers/translate";
import { ALERT } from "@constants";
import "./index.sass";

const AMOUNT_OPTIONS = [150, 300, 700, 1000];

const stripAmountFormatting = (value) => String(value ?? "").replace(/,/g, "");

const formatAmountInput = (value) => {
  const stripped = stripAmountFormatting(value).replace(/[^\d.]/g, "");

  if (!stripped) {
    return "";
  }

  const dotIndex = stripped.indexOf(".");

  const intRaw = dotIndex === -1 ? stripped : stripped.slice(0, dotIndex);

  const decRaw =
    dotIndex === -1 ? "" : stripped.slice(dotIndex + 1).replace(/\./g, "");

  const intDigits = intRaw.replace(/\D/g, "");

  const decDigits = decRaw.replace(/\D/g, "").slice(0, 2);

  const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (dotIndex !== -1) {
    return decDigits.length > 0
      ? `${formattedInt}.${decDigits}`
      : `${formattedInt}.`;
  }

  return formattedInt;
};

const PaymentMethodIcon = ({ icon, title }) => {
  if (!icon) return null;

  const isImage = typeof icon === "string";

  if (isImage) {
    return (
      <span className="payment-details-step__method-icon-wrap">
        <img
          className="payment-details-step__method-icon"
          src={icon}
          alt={title || ""}
        />
      </span>
    );
  }

  const Icon = icon;

  return (
    <span className="payment-details-step__method-svg-icon-wrap">
      <Icon className="payment-details-step__method-svg-icon" />
    </span>
  );
};

const PaymentDetailsStep = ({
  selectedMethod,
  amount,
  depositQuote,
  onAmountChange,
  onGetDepositQuote,
  onBack,
  onNext,
  loading = false,
  depositError = "",
}) => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const onGetDepositQuoteRef = useRef(onGetDepositQuote);

  const methodName =
    selectedMethod?.title ||
    selectedMethod?.name ||
    selectedMethod?.label ||
    __("wallet.deposit.payment_method");

  const methodIcon =
    selectedMethod?.icon ||
    selectedMethod?.logo ||
    selectedMethod?.image ||
    null;

  const asset = selectedMethod?.asset || "USDT";
  const commission = selectedMethod?.commission || "0%";
  const minDepositAmount = selectedMethod?.minDepositAmount || "1,330 USD";
  const processingTime = selectedMethod?.processingTime || "Instantly";
  const network = selectedMethod?.network || "";

  const openSupportChat = () => {
    document.getElementById("sendpulse-chat-wrapper")?.classList.add("active");
  };

  const showErrorAlert = (message) => {
    dispatch({
      type: ALERT.ALERT_ERROR,
      payload: message,
    });
  };

  const parseAmount = (value) => {
    if (value === null || value === undefined) return 0;

    const normalizedValue = stripAmountFormatting(value).replace(/[^\d.]/g, "");
    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const parseCommissionPercent = (value) => {
    if (value === null || value === undefined) return 0;

    const normalizedValue = String(value)
      .replace(",", ".")
      .replace(/[^\d.]/g, "");
    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const formatAmount = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0";

    return numberValue.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const amountNumber = useMemo(() => parseAmount(amount), [amount]);

  const minDepositNumber = useMemo(
    () => parseAmount(minDepositAmount),
    [minDepositAmount],
  );

  const quoteUsdAmount = useMemo(
    () => parseAmount(depositQuote?.usd_amount),
    [depositQuote?.usd_amount],
  );

  const quoteMinUsd = useMemo(
    () => parseAmount(depositQuote?.min_usd),
    [depositQuote?.min_usd],
  );

  const quoteMeetsMinimum = depositQuote?.meets_minimum;

  const receiveUsdAmount = quoteUsdAmount || amountNumber;
  const minDepositUsdNumber = quoteMinUsd || minDepositNumber;

  const commissionPercent = useMemo(
    () => parseCommissionPercent(commission),
    [commission],
  );

  const commissionAmount = useMemo(() => {
    return (amountNumber * commissionPercent) / 100;
  }, [amountNumber, commissionPercent]);

  const totalAmountToPay = useMemo(() => {
    return amountNumber + commissionAmount;
  }, [amountNumber, commissionAmount]);

  useEffect(() => {
    onGetDepositQuoteRef.current = onGetDepositQuote;
  }, [onGetDepositQuote]);

  useEffect(() => {
    if (!amountNumber || !selectedMethod?.asset || !selectedMethod?.network) {
      return undefined;
    }

    const timer = setTimeout(() => {
      onGetDepositQuoteRef.current?.({
        asset: selectedMethod.asset,
        network: selectedMethod.network,
        usd_amount: amountNumber,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [amountNumber, selectedMethod?.asset, selectedMethod?.network]);

  const handleAmountChange = (e) => {
    onAmountChange(formatAmountInput(e.target.value));
  };

  const handleSelectAmount = (value) => {
    onAmountChange(formatAmountInput(value));
  };

  const handleContinue = () => {
    if (loading) return;

    if (!amountNumber) {
      showErrorAlert(__("wallet.deposit.amount_required"));
      return;
    }

    if (quoteMeetsMinimum === false || amountNumber < minDepositUsdNumber) {
      showErrorAlert(
        __("wallet.deposit.minimum_deposit_amount_is") +
          ` ${formatAmount(minDepositUsdNumber)} USD`,
      );
      return;
    }

    onNext?.({
      amount: amountNumber,
      usdAmount: receiveUsdAmount,
      asset,
      commissionAmount,
      commissionPercent,
      totalAmountToPay,
    });
  };

  return (
    <div className="payment-details-step">
      <div className="payment-details-step__main">
        <div className="payment-details-step__card">
          <button
            type="button"
            className="payment-details-step__back"
            onClick={onBack}
            disabled={loading}
          >
            <svg
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 12.6668C9.87202 12.6668 9.744 12.6182 9.64666 12.5202L4.98 7.85353C4.78466 7.6582 4.78466 7.34151 4.98 7.14617L9.64666 2.47951C9.842 2.28417 10.1587 2.28417 10.354 2.47951C10.5494 2.67484 10.5494 2.99153 10.354 3.18686L6.04071 7.50018L10.354 11.8135C10.5494 12.0088 10.5494 12.3255 10.354 12.5208C10.256 12.6182 10.128 12.6668 10 12.6668Z"
                fill="#FCFCFD"
              />
            </svg>
            {__("wallet.deposit.back")}
          </button>

          <div className="payment-details-step__field">
            <p className="payment-details-step__label">
              {__("wallet.deposit.payment_method")}
            </p>

            <div className="payment-details-step__method">
              <PaymentMethodIcon icon={methodIcon} title={methodName} />

              <span>{methodName}</span>
            </div>
          </div>

          <div className="payment-details-step__info">
            <div className="payment-details-step__info-labels">
              <p>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8.66667 11.6667C8.66667 12.0347 8.368 12.3333 8 12.3333C7.632 12.3333 7.33333 12.0347 7.33333 11.6667C7.33333 11.2987 7.632 11 8 11C8.368 11 8.66667 11.2987 8.66667 11.6667ZM6 8.16667C6 7.064 6.89733 6.16667 8 6.16667C9.10267 6.16667 10 7.064 10 8.16667C10 9.26933 9.10267 10.1667 8 10.1667C6.89733 10.1667 6 9.26933 6 8.16667ZM7 8.16667C7 8.718 7.44867 9.16667 8 9.16667C8.55133 9.16667 9 8.718 9 8.16667C9 7.61533 8.55133 7.16667 8 7.16667C7.44867 7.16667 7 7.61533 7 8.16667ZM12.1667 4.66667V12C12.1667 13.612 11.2787 14.5 9.66667 14.5H6.33333C4.72133 14.5 3.83333 13.612 3.83333 12V4.66667C3.83333 4.39067 4.05733 4.16667 4.33333 4.16667H11.6667C11.9427 4.16667 12.1667 4.39067 12.1667 4.66667ZM11.1667 5.16667H4.83333V12C4.83333 13.0513 5.282 13.5 6.33333 13.5H9.66667C10.718 13.5 11.1667 13.0513 11.1667 12V5.16667ZM13 1.5H3C2.06067 1.5 1.5 2.06067 1.5 3V7C1.5 7.774 1.88533 8.304 2.55733 8.45467C2.82733 8.51334 3.094 8.34601 3.15466 8.07601C3.21533 7.80668 3.04599 7.53935 2.77665 7.47868C2.65999 7.45268 2.50065 7.41667 2.50065 7V3C2.50065 2.60733 2.60798 2.5 3.00065 2.5H13.0007C13.3933 2.5 13.5007 2.60733 13.5007 3V7C13.5007 7.41667 13.3413 7.45268 13.2247 7.47868C12.9553 7.53935 12.786 7.80601 12.8467 8.07601C12.8987 8.30868 13.1053 8.46667 13.3333 8.46667C13.37 8.46667 13.406 8.46267 13.4433 8.45467C14.1153 8.304 14.5007 7.774 14.5007 7V3C14.5 2.06067 13.9393 1.5 13 1.5Z"
                    fill="#888888"
                  />
                </svg>
                {__("wallet.deposit.commission")}:
              </p>

              <p>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12.5 4.198V4C12.5 2.388 11.612 1.5 10 1.5H3.33333C2.322 1.5 1.5 2.322 1.5 3.33333V12C1.5 13.612 2.388 14.5 4 14.5H12C13.612 14.5 14.5 13.612 14.5 12V6.66667C14.5 5.23 13.7947 4.36866 12.5 4.198ZM13.5 10.5H11C10.3567 10.5 9.83333 9.97667 9.83333 9.33333C9.83333 8.69 10.3567 8.16667 11 8.16667H13.5V10.5ZM3.33333 2.5H10C11.0513 2.5 11.5 2.94867 11.5 4V4.16667H3.33333C2.874 4.16667 2.5 3.79267 2.5 3.33333C2.5 2.874 2.874 2.5 3.33333 2.5ZM12 13.5H4C2.94867 13.5 2.5 13.0513 2.5 12V4.96598C2.75 5.09398 3.03333 5.16667 3.33333 5.16667H12C13.0513 5.16667 13.5 5.61533 13.5 6.66667V7.16667H11C9.80533 7.16667 8.83333 8.13867 8.83333 9.33333C8.83333 10.528 9.80533 11.5 11 11.5H13.5V12C13.5 13.0513 13.0513 13.5 12 13.5ZM11.34 8.66667H11.3467C11.7153 8.66667 12.0133 8.96533 12.0133 9.33333C12.0133 9.70133 11.7153 10 11.3467 10C10.9787 10 10.6767 9.70133 10.6767 9.33333C10.6767 8.96533 10.972 8.66667 11.34 8.66667Z"
                    fill="#888888"
                  />
                </svg>
                {__("wallet.deposit.minimum_deposit_amount")}:
              </p>

              <p>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M7.99992 0.833496C4.04792 0.833496 0.833252 4.04816 0.833252 8.00016C0.833252 11.9522 4.04792 15.1668 7.99992 15.1668C11.9519 15.1668 15.1666 11.9522 15.1666 8.00016C15.1666 4.04816 11.9519 0.833496 7.99992 0.833496ZM7.99992 14.1668C4.59925 14.1668 1.83325 11.4008 1.83325 8.00016C1.83325 4.5995 4.59925 1.8335 7.99992 1.8335C11.4006 1.8335 14.1666 4.5995 14.1666 8.00016C14.1666 11.4008 11.4006 14.1668 7.99992 14.1668ZM10.3533 9.64681C10.5486 9.84214 10.5486 10.1588 10.3533 10.3542C10.2559 10.4515 10.1279 10.5008 9.99992 10.5008C9.87192 10.5008 9.7439 10.4522 9.64657 10.3542L7.64657 8.35417C7.55257 8.26017 7.49992 8.13281 7.49992 8.00081V4.66748C7.49992 4.39148 7.72392 4.16748 7.99992 4.16748C8.27592 4.16748 8.49992 4.39148 8.49992 4.66748V7.79346L10.3533 9.64681Z"
                    fill="#888888"
                  />
                </svg>
                {__("wallet.deposit.processing_time")}:
              </p>
            </div>

            <div className="payment-details-step__info-values">
              <p>
                {commission}
                {commissionPercent > 0 &&
                  ` (${formatAmount(commissionAmount)} USD)`}
              </p>
              <p>{formatAmount(minDepositUsdNumber)} USD</p>
              <p>{processingTime}</p>
            </div>
          </div>

          <div className="payment-details-step__field">
            <p className="payment-details-step__label">
              {__("wallet.deposit.amount")}
            </p>

            <div className="payment-details-step__amount-control">
              <span className="payment-details-step__amount-currency">$</span>
              <span className="payment-details-step__amount-divider" />

              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="payment-details-step__amount-input"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0"
                disabled={loading}
              />
            </div>
          </div>

          <div className="payment-details-step__amount-options">
            {AMOUNT_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                className="payment-details-step__amount-option"
                onClick={() => handleSelectAmount(value)}
                disabled={loading}
              >
                {value} USD
              </button>
            ))}
          </div>

          <div className="payment-details-step__network">
            <p className="payment-details-step__network-info">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 15.1669C7.94667 15.1669 7.89329 15.1582 7.84196 15.1409C6.78263 14.7875 1.5 12.7482 1.5 7.25887V3.33357C1.5 3.09557 1.66802 2.8895 1.90202 2.8435C5.15535 2.19283 6.3087 1.6209 7.76937 0.896236C7.91004 0.826236 8.09664 0.816958 8.2373 0.886958C9.67797 1.61296 10.816 2.18683 14.0986 2.8435C14.3326 2.89017 14.5007 3.09557 14.5007 3.33357V7.25952C14.5007 12.7488 9.21802 14.7882 8.15869 15.1415C8.10669 15.1582 8.05333 15.1669 8 15.1669ZM2.5 3.74161V7.25887C2.5 11.8355 6.77867 13.699 8 14.137C9.22133 13.699 13.5 11.8349 13.5 7.25887V3.74161C10.5527 3.12495 9.31326 2.54617 8.00993 1.89217C6.62526 2.57683 5.42333 3.13095 2.5 3.74161ZM7.69466 9.68692L10.3613 7.02026C10.5567 6.82493 10.5567 6.50824 10.3613 6.3129C10.166 6.11757 9.84931 6.11757 9.65397 6.3129L7.34066 8.62622L6.36068 7.64624C6.16534 7.4509 5.84865 7.4509 5.65332 7.64624C5.45799 7.84157 5.45799 8.15826 5.65332 8.35359L6.98665 9.68692C7.08399 9.78426 7.21201 9.83357 7.34001 9.83357C7.46801 9.83357 7.59733 9.78493 7.69466 9.68692Z"
                  fill="#FF6C22"
                />
              </svg>
              {__("wallet.deposit.network")}: <span>{network}</span>
            </p>

            <p className="payment-details-step__network-confirmation">
              {__("wallet.deposit.network_confirmation")}
            </p>
          </div>
        </div>

        <div className="payment-details-step__side">
          <div className="payment-details-step__card payment-details-step__summary">
            <p className="payment-details-step__summary-label">
              {__("wallet.deposit.you_receive")}
            </p>

            <p className="payment-details-step__summary-amount">
              {formatAmount(receiveUsdAmount)} USD
            </p>

            <button
              type="button"
              className="payment-details-step__continue"
              onClick={handleContinue}
              disabled={loading}
            >
              {loading ? (
                __("common.loading")
              ) : (
                <>
                  {__("wallet.deposit.continue_and_pay")}{" "}
                  {formatAmount(totalAmountToPay)} USD
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M5.99997 13.1665C5.87197 13.1665 5.74396 13.1179 5.64662 13.0199C5.45129 12.8245 5.45129 12.5078 5.64662 12.3125L9.95994 7.9992L5.64662 3.68589C5.45129 3.49055 5.45129 3.17386 5.64662 2.97853C5.84196 2.7832 6.15865 2.7832 6.35398 2.97853L11.0206 7.6452C11.216 7.84053 11.216 8.15722 11.0206 8.35256L6.35398 13.0192C6.25598 13.1179 6.12797 13.1665 5.99997 13.1665Z"
                      fill="white"
                    />
                  </svg>
                </>
              )}
            </button>

            {depositError && (
              <p className="payment-details-step__amount-error">
                {depositError}
              </p>
            )}
          </div>

          <div className="payment-details-step__help">
            <p>{__("wallet.deposit.need_help")}</p>
            <p>{__("wallet.deposit.help_text")}</p>
          </div>

          <button
            type="button"
            className="payment-details-step__contact-support"
            onClick={openSupportChat}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M21.1599 16.634C21.5399 15.094 21.75 13.505 21.75 12C21.75 6.624 17.376 2.25 12 2.25C6.624 2.25 2.25 6.624 2.25 12C2.25 13.505 2.46009 15.093 2.84009 16.634C2.84709 16.667 2.846 16.6999 2.854 16.7319C2.872 16.8049 2.89604 16.8721 2.91504 16.9441C2.93704 17.0291 2.95405 17.116 2.97705 17.2C2.98205 17.22 2.99693 17.233 3.00293 17.252C3.09793 17.598 3.18912 17.9489 3.29712 18.2759L3.77002 19.8091C4.13402 20.9881 5.22588 21.749 6.40088 21.749C6.66688 21.749 6.9359 21.7099 7.2019 21.6289L8.16699 21.334C8.61399 21.198 8.9819 20.895 9.2019 20.481C9.4209 20.068 9.4671 19.5949 9.3291 19.1479L7.84497 14.292C7.70797 13.845 7.40594 13.4781 6.99194 13.2581C6.57794 13.0381 6.10196 12.9931 5.65796 13.1311L4.71606 13.4199C4.39906 13.5169 4.11597 13.673 3.86597 13.864C3.79597 13.234 3.75 12.607 3.75 11.999C3.75 7.45002 7.451 3.74902 12 3.74902C16.549 3.74902 20.25 7.45002 20.25 11.999C20.25 12.608 20.204 13.235 20.134 13.865C19.884 13.674 19.6009 13.5179 19.2839 13.4209L18.342 13.1321C17.899 12.9951 17.4231 13.04 17.0081 13.259C16.5951 13.478 16.292 13.845 16.155 14.293L14.6709 19.147C14.5329 19.594 14.5791 20.068 14.7981 20.48C15.0181 20.894 15.386 21.196 15.832 21.333L16.7981 21.6279C17.0641 21.7099 17.3331 21.748 17.5991 21.748C18.7741 21.748 19.866 20.9871 20.23 19.8081L20.7009 18.283C20.8109 17.951 20.8999 17.6059 20.9929 17.2629C21.0019 17.2409 21.0159 17.223 21.0229 17.199C21.0459 17.115 21.063 17.0281 21.085 16.9431C21.104 16.8711 21.128 16.802 21.146 16.73C21.154 16.699 21.1529 16.667 21.1599 16.634ZM5.15503 14.855L6.09692 14.5659C6.12292 14.5579 6.1479 14.5549 6.1709 14.5549C6.2229 14.5549 6.26409 14.572 6.28809 14.584C6.32209 14.602 6.38391 14.646 6.40991 14.731L7.89404 19.5869C7.92004 19.6719 7.894 19.7419 7.875 19.7759C7.857 19.8099 7.81305 19.8709 7.72705 19.8979L6.76294 20.1931C6.10294 20.3951 5.4049 20.022 5.2019 19.365L4.72412 17.8169C4.58812 17.4079 4.47204 16.9861 4.36304 16.5601C4.34304 16.4831 4.32588 16.4061 4.30688 16.3291C4.17488 15.6881 4.53503 15.046 5.15503 14.855ZM19.2729 17.8191C19.2709 17.8241 19.2701 17.829 19.2681 17.834L18.7949 19.3669C18.5929 20.0239 17.8929 20.3951 17.2339 20.1951L16.269 19.8999C16.183 19.8739 16.1391 19.8131 16.1221 19.7781C16.1031 19.7441 16.077 19.6739 16.103 19.5879L17.5869 14.7329C17.6129 14.6469 17.675 14.603 17.709 14.585C17.733 14.572 17.7739 14.5559 17.8259 14.5559C17.8489 14.5559 17.8739 14.5589 17.8999 14.5669L18.842 14.8569C19.462 15.0469 19.822 15.689 19.688 16.332C19.668 16.409 19.6521 16.487 19.6321 16.563C19.5261 16.988 19.4089 17.4111 19.2729 17.8191Z"
                fill="#FF6C22"
              />
            </svg>

            <p>{__("wallet.deposit.contact_support")}</p>

            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M7.50006 16.4581C7.34006 16.4581 7.18004 16.3973 7.05837 16.2748C6.8142 16.0307 6.8142 15.6348 7.05837 15.3906L12.45 9.999L7.05837 4.60736C6.8142 4.36319 6.8142 3.96733 7.05837 3.72316C7.30254 3.479 7.6984 3.479 7.94257 3.72316L13.7759 9.5565C14.0201 9.80066 14.0201 10.1965 13.7759 10.4407L7.94257 16.274C7.82007 16.3974 7.66006 16.4581 7.50006 16.4581Z"
                fill="#FF6C22"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsStep;